export const ALL_PERMISSIONS = [
  // ─── Geral ───
  'dashboard.view',
  'security.incidentes-escola.view',
  // ─── Gestão Escolar e Administrativo ───
  'escola.manage',
  'equipe.view',
  'equipe.manage',
  'gestao.view',
  'gestao.manage',
  'configuracoes.manage',
  // ─── Estrutura Acadêmica ───
  'turnos.view',
  'turnos.manage',
  'materias.view',
  'materias.manage',
  'turmas.view',
  'turmas.manage',
  'horarios.view',
  'horarios.manage',
  // ─── Cadastros ───
  'professores.view',
  'professores.manage',
  'alunos.view',
  'alunos.manage',
  'responsaveis.view',
  'responsaveis.manage',
  // ─── Operação e Portaria ───
  'chamadas.view',
  'chamadas.manage',
  'dispositivos.view',
  'dispositivos.manage',
  'acesso.view',
  'acesso.manage',
  // ─── Pedagógico ───
  'avaliacoes.view',
  'avaliacoes.manage',
  'observacoes.view',
  'observacoes.manage',
  'relatorios.view',
  // ─── Comunicação e Mídias ───
  'comunicados.view',
  'comunicados.manage',
  'comunicados.stats',
  'comunicados.tv_manage',
  'comunicados.config',
  'midias.view',
  'midias.manage',
] as const;

export type PermissionKey = (typeof ALL_PERMISSIONS)[number];
export type PermissionMap = Record<PermissionKey, boolean>;

/** Normaliza rótulos de cargo (ex.: "Diretor(a)", "Vice-Diretor") para chaves canônicas. */
export function normalizeEscolaCargo(cargo?: string | null): string {
  const c = (cargo || '').trim().toUpperCase();
  if (!c) return '';
  if (c === 'VICE-DIRETOR' || c.includes('VICE')) return 'VICE_DIRETOR';
  if (c === 'DIRETOR' || c.includes('DIRETOR')) return 'DIRETOR';
  if (c === 'COORDENACAO' || c.includes('COORDEN') || c === 'SUPERVISOR')
    return 'COORDENACAO';
  if (c === 'SECRETARIA' || c.includes('SECRETAR')) return 'SECRETARIA';
  return c;
}

export function isEscolaDiretorCargo(cargo?: string | null): boolean {
  const norm = normalizeEscolaCargo(cargo);
  return norm === 'DIRETOR' || norm === 'VICE_DIRETOR';
}

const emptyPermissions = (): PermissionMap =>
  Object.fromEntries(ALL_PERMISSIONS.map((key) => [key, false])) as PermissionMap;

const fullPermissions = (): PermissionMap =>
  Object.fromEntries(ALL_PERMISSIONS.map((key) => [key, true])) as PermissionMap;

const withDefaults = (partial: Partial<PermissionMap>): PermissionMap => ({
  ...emptyPermissions(),
  ...partial,
});

export function getPermissionsByRole(perfil?: string | null, cargo?: string | null): PermissionMap {
  const p = (perfil || '').toUpperCase();
  const c = (cargo || '').toUpperCase();
  const normCargo = normalizeEscolaCargo(cargo);

  // ── PROFESSOR (prioridade: professor NUNCA pode ter permissão de Diretor) ──
  if (p === 'PROFESSOR' || c === 'PROFESSOR' || normCargo === 'PROFESSOR') {
    return withDefaults({
      'dashboard.view': true,
      'turmas.view': true,
      'horarios.view': true,
      'materias.view': true,
      'alunos.view': true,
      'responsaveis.view': true,
      'professores.view': true,
      'chamadas.view': true,
      'chamadas.manage': true,
      'avaliacoes.view': true,
      'avaliacoes.manage': true,
      'observacoes.view': true,
      'observacoes.manage': true,
      'relatorios.view': true,
      'comunicados.view': true,
      'midias.view': true,
    });
  }

  // ── RESPONSÁVEL / ALUNO ──
  if (p === 'RESPONSAVEL' || p === 'ALUNO') {
    return withDefaults({
      'dashboard.view': true,
      'chamadas.view': true,
      'avaliacoes.view': true,
      'relatorios.view': true,
      'comunicados.view': true,
    });
  }

  const isEscolaDiretorOuMaster =
    p === 'ADMIN' ||
    p === 'MASTER' ||
    p === 'SISTEMA' ||
    normCargo === 'DIRETOR' ||
    normCargo === 'VICE_DIRETOR' ||
    (p === 'ESCOLA' && (!normCargo || normCargo === 'DIRETOR' || normCargo === 'VICE_DIRETOR'));

  if (isEscolaDiretorOuMaster) {
    return fullPermissions();
  }

  // ── COORDENAÇÃO ──
  if (normCargo === 'COORDENACAO' || p === 'COORDENACAO') {
    return withDefaults({
      'dashboard.view': true,
      'security.incidentes-escola.view': true,
      'equipe.view': true,
      'gestao.view': true,
      'turnos.view': true,
      'turnos.manage': true,
      'materias.view': true,
      'materias.manage': true,
      'turmas.view': true,
      'turmas.manage': true,
      'horarios.view': true,
      'horarios.manage': true,
      'professores.view': true,
      'alunos.view': true,
      'responsaveis.view': true,
      'responsaveis.manage': true,
      'chamadas.view': true,
      'chamadas.manage': true,
      'dispositivos.view': true,
      'acesso.view': true,
      'avaliacoes.view': true,
      'avaliacoes.manage': true,
      'observacoes.view': true,
      'observacoes.manage': true,
      'relatorios.view': true,
      'comunicados.view': true,
      'comunicados.manage': true,
      'comunicados.stats': true,
      'comunicados.tv_manage': true,
      'midias.view': true,
      'midias.manage': true,
    });
  }

  // ── SECRETARIA ──
  if (normCargo === 'SECRETARIA' || p === 'SECRETARIA') {
    return withDefaults({
      'dashboard.view': true,
      'security.incidentes-escola.view': true,
      'equipe.view': true,
      'gestao.view': true,
      'turnos.view': true,
      'materias.view': true,
      'turmas.view': true,
      'horarios.view': true,
      'professores.view': true,
      'professores.manage': true,
      'alunos.view': true,
      'alunos.manage': true,
      'responsaveis.view': true,
      'responsaveis.manage': true,
      'chamadas.view': true,
      'chamadas.manage': true,
      'dispositivos.view': true,
      'dispositivos.manage': true,
      'acesso.view': true,
      'acesso.manage': true,
      'avaliacoes.view': true,
      'relatorios.view': true,
      'comunicados.view': true,
      'comunicados.manage': true,
      'midias.view': true,
      'midias.manage': true,
    });
  }

  // ── PROFESSOR ──
  if (p === 'PROFESSOR') {
    return withDefaults({
      'dashboard.view': true,
      'turmas.view': true,
      'horarios.view': true,
      'alunos.view': true,
      'responsaveis.view': true,
      'professores.view': true,
      'chamadas.view': true,
      'chamadas.manage': true,
      'avaliacoes.view': true,
      'avaliacoes.manage': true,
      'observacoes.view': true,
      'observacoes.manage': true,
      'relatorios.view': true,
      'comunicados.view': true,
      'midias.view': true,
    });
  }

  // ── MUNICIPAL ──
  if (p === 'MUNICIPAL') {
    if (c === 'COMUNICACAO') {
      return withDefaults({
        'dashboard.view': true,
        'comunicados.view': true,
        'comunicados.manage': true,
        'midias.view': true,
        'midias.manage': true,
      });
    }
    if (c === 'CONSULTA') {
      return withDefaults({
        'dashboard.view': true,
        'gestao.view': true,
        'comunicados.view': true,
        'relatorios.view': true,
      });
    }
    return withDefaults({
      'dashboard.view': true,
      'security.incidentes-escola.view': true,
      'escola.manage': true,
      'equipe.view': true,
      'equipe.manage': true,
      'gestao.view': true,
      'gestao.manage': true,
      'comunicados.view': true,
      'comunicados.manage': true,
      'midias.view': true,
      'midias.manage': true,
      'relatorios.view': true,
      'configuracoes.manage': true,
    });
  }

  // ── ESTADUAL ──
  if (p === 'ESTADUAL') {
    if (c === 'ANALISTA' || c === 'CONSULTA') {
      return withDefaults({
        'dashboard.view': true,
        'gestao.view': true,
        'comunicados.view': true,
        'relatorios.view': true,
      });
    }
    if (c === 'COMUNICACAO') {
      return withDefaults({
        'dashboard.view': true,
        'comunicados.view': true,
        'comunicados.manage': true,
        'midias.view': true,
        'midias.manage': true,
      });
    }
    return withDefaults({
      'dashboard.view': true,
      'security.incidentes-escola.view': true,
      'escola.manage': true,
      'equipe.view': true,
      'equipe.manage': true,
      'gestao.view': true,
      'gestao.manage': true,
      'comunicados.view': true,
      'comunicados.manage': true,
      'midias.view': true,
      'midias.manage': true,
      'relatorios.view': true,
      'configuracoes.manage': true,
    });
  }

  // ── REDE ──
  if (p === 'REDE') {
    if (c === 'CONSULTA') {
      return withDefaults({
        'dashboard.view': true,
        'gestao.view': true,
        'comunicados.view': true,
        'relatorios.view': true,
      });
    }
    if (c === 'SUPERVISOR') {
      return withDefaults({
        'dashboard.view': true,
        'equipe.view': true,
        'gestao.view': true,
        'comunicados.view': true,
        'relatorios.view': true,
      });
    }
    return withDefaults({
      'dashboard.view': true,
      'security.incidentes-escola.view': true,
      'escola.manage': true,
      'equipe.view': true,
      'equipe.manage': true,
      'gestao.view': true,
      'gestao.manage': true,
      'comunicados.view': true,
      'comunicados.manage': true,
      'midias.view': true,
      'midias.manage': true,
      'relatorios.view': true,
      'configuracoes.manage': true,
    });
  }

  // ── RESPONSAVEL ──
  if (p === 'RESPONSAVEL') {
    return withDefaults({
      'dashboard.view': true,
      'responsaveis.view': true,
      'responsaveis.manage': true,
      'chamadas.view': true,
      'avaliacoes.view': true,
      'observacoes.view': true,
      'comunicados.view': true,
      'relatorios.view': true,
    });
  }

  // ── ALUNO ──
  if (p === 'ALUNO') {
    return withDefaults({
      'dashboard.view': true,
      'chamadas.view': true,
      'avaliacoes.view': true,
      'observacoes.view': true,
      'comunicados.view': true,
      'relatorios.view': true,
    });
  }

  // ── PUBLICO ──
  if (p === 'PUBLICO') {
    return withDefaults({
      'dashboard.view': true,
    });
  }

  return withDefaults({
    'dashboard.view': true,
    'equipe.view': true,
    'turnos.view': true,
    'materias.view': true,
    'turmas.view': true,
    'horarios.view': true,
    'professores.view': true,
    'alunos.view': true,
    'responsaveis.view': true,
    'chamadas.view': true,
    'avaliacoes.view': true,
    'relatorios.view': true,
    'comunicados.view': true,
    'midias.view': true,
    'acesso.view': true,
    'dispositivos.view': true,
  });
}

export function resolvePermissions(user?: {
  perfil?: string | null;
  cargo?: string | null;
  preferencias?: Record<string, any> | null;
} | null): PermissionMap {
  if (!user) return emptyPermissions();
  
  const p = (user.perfil || '').toUpperCase();
  if (p === 'ADMIN' || p === 'MASTER' || p === 'SISTEMA') {
    return fullPermissions();
  }
  
  const base = getPermissionsByRole(user.perfil, user.cargo);
  const custom = user.preferencias?.permissoes;
  const merged = { ...base };
  
  if (custom && typeof custom === 'object') {
    for (const key of ALL_PERMISSIONS) {
      if (typeof custom[key] === 'boolean') {
        merged[key] = custom[key];
      }
    }
  }

  // Diretor ou administrador pleno da escola (sem subcargo restritivo) nunca perde leitura/gestão de equipe
  if (p === 'ESCOLA' && (!user.cargo || isEscolaDiretorCargo(user.cargo))) {
    merged['equipe.view'] = true;
    merged['equipe.manage'] = true;
  }
  
  return merged;
}

export function hasPermission(
  user: { perfil?: string | null; cargo?: string | null; preferencias?: Record<string, any> | null } | null | undefined,
  permission: PermissionKey,
): boolean {
  return !!resolvePermissions(user)[permission];
}

const EQUIPE_ROUTE_FALLBACK: string[] = [
  '/equipe/dashboard',
  '/equipe/turnos',
  '/equipe/materias',
  '/equipe/turmas',
  '/equipe/professores',
  '/equipe/alunos',
  '/equipe/equipe',
  '/equipe/frequencia',
  '/equipe/chamadas-gestao',
  '/equipe/diario',
  '/equipe/relatorios',
  '/equipe/responsaveis',
  '/equipe/portaria',
  '/equipe/incidentes',
  '/equipe/comunicados',
  '/equipe/perfil',
  '/equipe/configuracoes',
];

/**
 * Rota do painel /equipe/*: combina o mapa granular (checkboxes) com o menu.
 * Perfis que não sejam ESCOLA mantêm acesso (rotas de equipe não se aplicam a eles do mesmo modo).
 */
export function canAccessEquipePath(
  path: string,
  user: { perfil?: string | null; cargo?: string | null; preferencias?: Record<string, any> | null } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.perfil && user.perfil !== 'ESCOLA') return true;

  const p = path.toLowerCase();
  if (!p.startsWith('/equipe')) return true;

  if (p.startsWith('/equipe/perfil') || p === '/equipe/perfil') return true;
  if (p.startsWith('/equipe/configuracoes') || p === '/equipe/configuracoes') return true;

  if (p === '/equipe' || p === '/equipe/') {
    return hasPermission(user, 'dashboard.view');
  }
  if (p.startsWith('/equipe/dashboard')) return hasPermission(user, 'dashboard.view');
  if (p.startsWith('/equipe/turnos')) return hasPermission(user, 'turnos.view');
  if (p.startsWith('/equipe/materias')) return hasPermission(user, 'materias.view');
  if (p.startsWith('/equipe/turmas')) return hasPermission(user, 'turmas.view');
  if (p.startsWith('/equipe/professores')) return hasPermission(user, 'professores.view');
  if (p.startsWith('/equipe/alunos')) return hasPermission(user, 'alunos.view');
  if (p.startsWith('/equipe/equipe')) return hasPermission(user, 'equipe.view');
  if (p.startsWith('/equipe/relatorios')) return hasPermission(user, 'relatorios.view');
  if (p.startsWith('/equipe/frequencia') || p.startsWith('/equipe/justificativas')) {
    return hasPermission(user, 'chamadas.view') || hasPermission(user, 'alunos.view');
  }
  if (p.startsWith('/equipe/chamadas-gestao')) {
    return hasPermission(user, 'chamadas.view');
  }
  if (p.startsWith('/equipe/diario')) {
    return hasPermission(user, 'avaliacoes.view') || hasPermission(user, 'chamadas.view');
  }
  if (p.startsWith('/equipe/responsaveis')) return hasPermission(user, 'responsaveis.view') || hasPermission(user, 'alunos.view');
  if (p.startsWith('/equipe/portaria')) return hasPermission(user, 'acesso.view') || hasPermission(user, 'dispositivos.view');
  if (p.startsWith('/equipe/incidentes')) return hasPermission(user, 'security.incidentes-escola.view');
  if (p.startsWith('/equipe/comunicados')) return hasPermission(user, 'comunicados.view');
  return hasPermission(user, 'dashboard.view');
}

export function getFirstAllowedEquipePath(
  user: { perfil?: string | null; cargo?: string | null; preferencias?: Record<string, any> | null } | null | undefined,
): string | null {
  if (!user) return null;
  if (user.perfil && user.perfil !== 'ESCOLA') return null;
  for (const route of EQUIPE_ROUTE_FALLBACK) {
    if (canAccessEquipePath(route, user)) return route;
  }
  return null;
}

/**
 * Aplica as regras de coerência RBAC ao alternar permissões:
 * 1. Ao ATIVAR uma permissão de gerenciar (*.manage), a permissão correspondente de visualizar (*.view) é ativada automaticamente.
 * 2. Ao DESATIVAR uma permissão de visualizar (*.view), a permissão correspondente de gerenciar (*.manage) é desativada automaticamente.
 */
export function togglePermissionWithCoherence<T extends Record<string, boolean> = PermissionMap>(
  currentPermissions: T,
  toggledKey: string,
  explicitValue?: boolean,
): T {
  const permMap = (currentPermissions || {}) as Record<string, boolean>;
  const nextValue = explicitValue !== undefined ? explicitValue : !permMap[toggledKey];
  const updated: Record<string, boolean> = { ...permMap, [toggledKey]: nextValue };

  // Se ativou um *.manage, ativa o *.view correspondente
  if (toggledKey.endsWith('.manage') && nextValue === true) {
    const viewKey = toggledKey.replace('.manage', '.view');
    if ((ALL_PERMISSIONS as readonly string[]).includes(viewKey)) {
      updated[viewKey] = true;
    }
  }

  // Se desativou um *.view, desativa o *.manage correspondente
  if (toggledKey.endsWith('.view') && nextValue === false) {
    const manageKey = toggledKey.replace('.view', '.manage');
    if ((ALL_PERMISSIONS as readonly string[]).includes(manageKey)) {
      updated[manageKey] = false;
    }
  }

  return updated as T;
}

